import metric from "./metric";

const METRIC_TYPES = {
    MEDIA_DOWNLOADED: 'd',
    MEDIA_DOWNLOADED_ZIP: 'dz',
    TIME_IN_ALBUM: 'ta',
    MEDIA_VIEWED: 'v',
    MEDIA_OPENED: 'o',
    SHARED_LINK_OPENED: 'slo',
    SHARED_LINK_CREATED: 'slc',
    BANNER_CLICKED: 'bc',
    BANNER_VIEWED: 'bv',
    ANONYMIZED_VIEWED: 'av',
    LEAD_ACQUIRED: 'la',
    SHARE_WITH_FRIEND_CREATED: 'swfc',
    CHECKIN_PAGE_OPENED: 'cpo',
}

const FIELD_NAMES = {
    METRIC_TYPES: 't',
    USER_ID: 'u',
    EVENT_ID: 'e',
    MEDIA_ID: 'm',
    ZIP_ID: 'z',
    TIME_IN_SECONDS: 's',
    TIMESTAMP: 'ts',
    ALBUM_ID: 'aid',
    BANNER_ID: 'bid',
    SHARE_TYPE: 'st',
    SHARED_BY_USER_ID: 'suid',
    SHORT_KEY: 'sk',
    SHORT_LONG_URL: 'sl',
    MEDIA_TYPE: 'mt',
    PLATFORM_TYPE: 'p',
    CHECKIN_TAGS: 'ct',
}

const PLATFORM_TYPE = {
    SITE: 's',
}

const SHARE_TYPES = {
    INSTAGRAM: 'i',
    FACEBOOK: 'f',
    WHATSAPP: 'w',
    TWITTER: 't',
    TELEGRAM: 'te',
    EMAIL: 'e',
    LINKEDIN: 'l',
}

const MEDIA_TYPE = {
    PHOTO: 'p',
    VIDEO: 'v',
}

export default class Message {

    static eventId = null
    static userId = null

    constructor(data, anonymous = false) {
        if (!anonymous) {
            if (Message.eventId === null) throw new Error('Event ID is not set for Metric Message')
            if (Message.userId === null) throw new Error('User ID is not set for Metric Message')
        }

        const log = {
            [FIELD_NAMES.TIMESTAMP]: Date.now().valueOf(),
            [FIELD_NAMES.PLATFORM_TYPE]: PLATFORM_TYPE.SITE,
            ...data
        }
        if (Message.eventId) log[FIELD_NAMES.EVENT_ID] = Message.eventId
        if (Message.userId) log[FIELD_NAMES.USER_ID] = Message.userId

        metric.log(log)
    }

    static setEventId(eventId) {
        Message.eventId = eventId
    }

    static setUserId(userId) {
        Message.userId = userId
    }

    static photoDownloaded(photo_id, is_video = false) {
        return new Message({
            [FIELD_NAMES.METRIC_TYPES]: METRIC_TYPES.MEDIA_DOWNLOADED,
            [FIELD_NAMES.MEDIA_ID]: photo_id,
            [FIELD_NAMES.MEDIA_TYPE]: is_video ? MEDIA_TYPE.VIDEO : MEDIA_TYPE.PHOTO
        })
    }

    static createShareIntent(photo_id, network, is_video = false) {
        network = convertNetworkType(network)
        if (!network) return

        return new Message({
            [FIELD_NAMES.METRIC_TYPES]: METRIC_TYPES.SHARED_LINK_CREATED,
            [FIELD_NAMES.MEDIA_ID]: photo_id,
            [FIELD_NAMES.SHARE_TYPE]: network,
            [FIELD_NAMES.SHARED_BY_USER_ID]: Message.userId,
            [FIELD_NAMES.MEDIA_TYPE]: is_video ? MEDIA_TYPE.VIDEO : MEDIA_TYPE.PHOTO
        })
    }

    static timeInAlbum(album_id, time_in_seconds) {
        return new Message({
            [FIELD_NAMES.METRIC_TYPES]: METRIC_TYPES.TIME_IN_ALBUM,
            [FIELD_NAMES.ALBUM_ID]: album_id,
            [FIELD_NAMES.TIME_IN_SECONDS]: time_in_seconds
        })
    }

    static mediaViewed(photo_id, is_video = false) {
        return new Message({
            [FIELD_NAMES.METRIC_TYPES]: METRIC_TYPES.MEDIA_VIEWED,
            [FIELD_NAMES.MEDIA_ID]: photo_id,
            [FIELD_NAMES.MEDIA_TYPE]: is_video ? MEDIA_TYPE.VIDEO : MEDIA_TYPE.PHOTO
        })
    }

    static mediaOpened(photo_id, is_video = false) {
        return new Message({
            [FIELD_NAMES.METRIC_TYPES]: METRIC_TYPES.MEDIA_OPENED,
            [FIELD_NAMES.MEDIA_ID]: photo_id,
            [FIELD_NAMES.MEDIA_TYPE]: is_video ? MEDIA_TYPE.VIDEO : MEDIA_TYPE.PHOTO
        })
    }

    // Banner presente nel checkin datail e' attualmente statico, necessaria implementazione lato backend
    static bannerViewed(banner_id) {
        return new Message({
            [FIELD_NAMES.METRIC_TYPES]: METRIC_TYPES.BANNER_VIEWED,
            [FIELD_NAMES.BANNER_ID]: banner_id
        })
    }

    static bannerClicked(banner_id) {
        return new Message({
            [FIELD_NAMES.METRIC_TYPES]: METRIC_TYPES.BANNER_CLICKED,
            [FIELD_NAMES.BANNER_ID]: banner_id
        })
    }

    static anonymizedInteraction(album_id) {
        return new Message({
            [FIELD_NAMES.METRIC_TYPES]: METRIC_TYPES.ANONYMIZED_VIEWED,
            [FIELD_NAMES.ALBUM_ID]: album_id
        })
    }

    static sharedLinkOpened(media_id, sharer_id, network, event_id, is_video) {
        network = convertNetworkType(network)
        if (!network) return
        return new Message({
            [FIELD_NAMES.METRIC_TYPES]: METRIC_TYPES.SHARED_LINK_OPENED,
            [FIELD_NAMES.SHARED_BY_USER_ID]: sharer_id,
            [FIELD_NAMES.MEDIA_ID]: media_id,
            [FIELD_NAMES.SHARE_TYPE]: network,
            [FIELD_NAMES.EVENT_ID]: event_id,
            [FIELD_NAMES.MEDIA_TYPE]: is_video ? MEDIA_TYPE.VIDEO : MEDIA_TYPE.PHOTO
        }, true)
    }

    static createShareWithFriend(network) {
        network = convertNetworkType(network)
        if (!network) return

        return new Message({
            [FIELD_NAMES.METRIC_TYPES]: METRIC_TYPES.SHARE_WITH_FRIEND_CREATED,
            [FIELD_NAMES.SHARE_TYPE]: network,
            [FIELD_NAMES.SHARED_BY_USER_ID]: Message.userId,
        })
    }

    static openedCheckinPreview(queryUrl) {
        const allowedParams = {
            'n': 'network',
            'utm_source': 'utm_source',
            'utm_medium': 'utm_medium',
            'utm_campaign': 'utm_campaign',
            'utm_id': 'utm_id',
            'utm_content': 'utm_content'
        }

        let params = {}
        Object.keys(allowedParams).forEach(key => {
            if (!queryUrl.hasOwnProperty(key)) {
                params[allowedParams[key]] = queryUrl[key]
            }
        })

        return new Message({
            [FIELD_NAMES.METRIC_TYPES]: METRIC_TYPES.CHECKIN_PAGE_OPENED,
            [FIELD_NAMES.CHECKIN_TAGS]: params,

        },true)
    }
}


const convertNetworkType = (network) => {
    if (network === 'instagram')
        return SHARE_TYPES.INSTAGRAM
    else if (network === 'facebook')
        return SHARE_TYPES.FACEBOOK
    else if (network === 'whatsapp')
        return SHARE_TYPES.WHATSAPP
    else if (network === 'twitter')
        return SHARE_TYPES.TWITTER
    else if (network === 'telegram')
        return SHARE_TYPES.TELEGRAM
    else if (network === 'email')
        return SHARE_TYPES.EMAIL
    else if (network === 'linkedin')
        return SHARE_TYPES.LINKEDIN
    else
        return null
}